// src/contexts/ToastContext.tsx
import React, {
  createContext, useContext, useState, useCallback,
  useEffect, useRef, ReactNode,
} from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number; // ms, 0 = persistent
  action?: { label: string; onClick: () => void };
}

interface ToastContextType {
  toast: (opts: Omit<Toast, 'id'>) => string;
  success: (title: string, message?: string) => string;
  error:   (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info:    (title: string, message?: string) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// ── Context ───────────────────────────────────────────────────────────────────
const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

// ── Config per type ───────────────────────────────────────────────────────────
const CONFIG: Record<ToastType, {
  icon: React.FC<{ size: number; className: string }>;
  bar: string;
  iconClass: string;
  bg: string;
  border: string;
}> = {
  success: {
    icon: ({ size, className }) => <CheckCircle size={size} className={className} />,
    bar:       'bg-emerald-500',
    iconClass: 'text-emerald-500',
    bg:        'bg-white dark:bg-[#111]',
    border:    'border-l-4 border-l-emerald-500',
  },
  error: {
    icon: ({ size, className }) => <XCircle size={size} className={className} />,
    bar:       'bg-red-500',
    iconClass: 'text-red-500',
    bg:        'bg-white dark:bg-[#111]',
    border:    'border-l-4 border-l-red-500',
  },
  warning: {
    icon: ({ size, className }) => <AlertTriangle size={size} className={className} />,
    bar:       'bg-amber-500',
    iconClass: 'text-amber-500',
    bg:        'bg-white dark:bg-[#111]',
    border:    'border-l-4 border-l-amber-500',
  },
  info: {
    icon: ({ size, className }) => <Info size={size} className={className} />,
    bar:       'bg-blue-500',
    iconClass: 'text-blue-500',
    bg:        'bg-white dark:bg-[#111]',
    border:    'border-l-4 border-l-blue-500',
  },
};

// ── Single toast item ─────────────────────────────────────────────────────────
const ToastItem: React.FC<{
  toast: Toast;
  onDismiss: (id: string) => void;
}> = ({ toast, onDismiss }) => {
  const cfg = CONFIG[toast.type];
  const Icon = cfg.icon;
  const duration = toast.duration ?? 4000;
  const [progress, setProgress] = useState(100);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef<number>(Date.now());
  const remainingRef = useRef<number>(duration);

  // Slide in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  // Progress bar countdown
  useEffect(() => {
    if (duration === 0) return;
    startRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct === 0) {
        clearInterval(intervalRef.current!);
        handleDismiss();
      }
    }, 30);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [duration]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  // Pause on hover
  const pauseProgress = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    remainingRef.current = (progress / 100) * duration;
  };

  const resumeProgress = () => {
    if (duration === 0) return;
    startRef.current = Date.now() - (duration - remainingRef.current);
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct === 0) {
        clearInterval(intervalRef.current!);
        handleDismiss();
      }
    }, 30);
  };

  return (
    <div
      onMouseEnter={pauseProgress}
      onMouseLeave={resumeProgress}
      className={`
        relative w-full max-w-sm overflow-hidden rounded-xl
        shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]
        border border-light-border dark:border-dark-border
        ${cfg.bg} ${cfg.border}
        transition-all duration-300 ease-out
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}
      `}
    >
      {/* Content */}
      <div className="flex items-start gap-3 px-4 py-3.5">
        <Icon size={20} className={`flex-shrink-0 mt-0.5 ${cfg.iconClass}`} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-light-text dark:text-dark-text leading-snug">
            {toast.title}
          </p>
          {toast.message && (
            <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5 leading-relaxed">
              {toast.message}
            </p>
          )}
          {toast.action && (
            <button
              onClick={() => { toast.action!.onClick(); handleDismiss(); }}
              className={`mt-2 text-xs font-semibold underline underline-offset-2 ${cfg.iconClass}`}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-0.5 rounded-md text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text hover:bg-light-border dark:hover:bg-dark-border transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Progress bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-light-border dark:bg-dark-border">
          <div
            className={`h-full ${cfg.bar} transition-none`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// ── Provider ──────────────────────────────────────────────────────────────────
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const toast = useCallback((opts: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts(prev => {
      // Cap at 5 toasts — remove oldest if over limit
      const next = [...prev, { ...opts, id }];
      return next.length > 5 ? next.slice(next.length - 5) : next;
    });
    return id;
  }, []);

  const success = useCallback((title: string, message?: string) =>
    toast({ type: 'success', title, message }), [toast]);

  const error = useCallback((title: string, message?: string) =>
    toast({ type: 'error', title, message, duration: 6000 }), [toast]);

  const warning = useCallback((title: string, message?: string) =>
    toast({ type: 'warning', title, message }), [toast]);

  const info = useCallback((title: string, message?: string) =>
    toast({ type: 'info', title, message }), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info, dismiss, dismissAll }}>
      {children}

      {/* Toast container — fixed bottom-left, stacks upward */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 left-4 z-[9999] flex flex-col-reverse gap-2 items-start pointer-events-none"
        style={{ maxWidth: 'calc(100vw - 2rem)' }}
      >
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto w-full max-w-sm">
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
